/**
 * AssignmentsDirectory
 *
 * Shared assignments-management surface — sub-tabs (Assignment Setup
 * vs. Assignments roster), a course-name search bar, and the two table
 * components mounted with the right scope.
 *
 * Two consumers today:
 *   1. The standalone Assignments page (`pages/Assignments.tsx`) —
 *      account/workspace-wide view, falls back to the global effective
 *      workspace id when no lock is provided. The page wires its own
 *      "Add Assignment" CTA into the PageHeader actions slot via the
 *      shared `addHandlerRef`.
 *   2. The workspace edit page's Assignments tab — drilled-in view
 *      for one workspace. Pass `workspaceIdLock`; the directory uses
 *      that id for every query AND renders its own inline Add button
 *      on the Setup sub-tab (no external CTA wiring needed).
 *
 * Pattern mirrors `UsersDirectory` so the two drill-in tabs (Users +
 * Assignments) on the workspace edit page feel like the same shape.
 */

import { useState } from 'react';
import { useEffectiveWorkspaceId } from '../../hooks/useEffectiveWorkspaceId';
import { AddIcon, BookmarkIcon, SearchIcon, SettingsIcon } from '../icons';
import {
  Box,
  Button,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
} from '../ui';
import AssignmentSetup from './AssignmentSetup';
import UserAssignmentsTable from './UserAssignmentsTable';

export interface AssignmentsDirectoryProps {
  /** When set, locks all queries to this workspace. Used by the
   *  workspace edit page's Assignments tab. Pass `null` to force
   *  "no workspace scope" (rare); leave undefined to fall through to
   *  the global effective workspace id. */
  workspaceIdLock?: number | null;
  /** Optional external trigger for the "create assignment" flow.
   *  When provided, the directory does NOT render its own Add button
   *  (the parent owns it, typically in a page header). When omitted,
   *  the directory renders an inline gradient Add button next to the
   *  search bar on the Setup sub-tab. */
  addHandlerRef?: React.MutableRefObject<(() => void) | null>;
}

export default function AssignmentsDirectory({
  workspaceIdLock,
  addHandlerRef,
}: AssignmentsDirectoryProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const fallbackWorkspaceId = useEffectiveWorkspaceId();
  // Lock wins when explicitly provided (including null); otherwise
  // fall back to the global effective workspace id.
  const effectiveWorkspaceId =
    workspaceIdLock !== undefined ? workspaceIdLock : fallbackWorkspaceId;

  // Internal Add button — fires when the parent didn't wire a ref.
  // We define a local ref so the inline button has somewhere to read
  // the handler from once AssignmentSetup registers it on mount.
  const internalAddRef = useState<{ current: (() => void) | null }>(() => ({
    current: null,
  }))[0];
  const handlerRefForChild = addHandlerRef ?? internalAddRef;
  const showInlineAdd = !addHandlerRef;

  return (
    <Box>
      {/* Sub-tabs — same labels and icons as the standalone page so
          the two surfaces read identically. */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_e, newValue) => setActiveTab(newValue)}>
          <Tab label="Assignment Setup" icon={<SettingsIcon />} iconPosition="start" />
          <Tab label="Assignments" icon={<BookmarkIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Search + optional inline Add — only relevant on the Setup
          tab. The Assignments roster owns its own search via
          PagedTable. */}
      {activeTab === 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search by course name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flex: 1, maxWidth: 600, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          {showInlineAdd && (
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => handlerRefForChild.current?.()}
              sx={{ px: 3, flexShrink: 0 }}
            >
              Add Assignment
            </Button>
          )}
        </Box>
      )}

      {/* Tab content — both tables already accept `workspaceId`, so
          scoping is a single prop pass. */}
      {activeTab === 0 ? (
        <AssignmentSetup
          courseId={null}
          searchQuery={searchQuery}
          workspaceId={effectiveWorkspaceId}
          hideAddFab
          addHandlerRef={handlerRefForChild}
        />
      ) : (
        <UserAssignmentsTable courseId={null} workspaceId={effectiveWorkspaceId} />
      )}
    </Box>
  );
}
